import { defineEvent, otto } from "@otto-ec/global-resources/nexus";
import { currentBreakpoint } from "@otto-ec/global-resources/viewport";
import { eventQBus } from "../../types/EventQBus";
import type { DirectDresonRuleResolveEvent, DresonRuleResolvedEvent, Page } from "../../types/FilterTypes";
import { toggles } from "../utils/Toggles";
import {
  createProductionSelectionDependencies,
  type ResolvedSelectionResponse,
  type SelectionDomAdapter,
  type SelectionResolver,
} from "./SelectionDependencies";
import type {
  ResolveInput as ProspectiveResolveInput,
  SelectionAssignment,
  SelectionChange,
  SelectionOperationOptions,
  SelectionPage,
  SelectionResult,
  SelectionState,
} from "@otto-ec/nexus-registry/selection";

type LegacyResolveOptions = {
  entryRule?: string;
};

type LegacyAssignOptions = {
  event: DirectDresonRuleResolveEvent;
};

export class Selection {
  private latestOperation = 0;
  private nextOperationId = 0;
  private initialized = false;
  private currentState: SelectionState | undefined;
  private readonly current = defineEvent<SelectionState>();
  private readonly changes = defineEvent<SelectionChange>();

  public constructor(
    private readonly dependencies: {
      resolver: SelectionResolver;
      dom: SelectionDomAdapter;
    } = createProductionSelectionDependencies(),
  ) {}

  public init(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    if (toggles.isActive("REPTILE_1147_NEXUS_SELECTION")) {
      otto.selection.assignNamespace({
        resolve: this.resolve,
        assign: this.assign,
        current: this.current,
        changes: this.changes,
      });
    }

    this.bootstrap();
  }

  public readonly resolve: (
    input: ProspectiveResolveInput,
    options?: SelectionOperationOptions,
  ) => Promise<SelectionResult> = async (input, options) => {
    const result = await this.resolveOperation(input, options, this.currentState?.entryRule);
    if (result.count !== 0) {
      this.emitResolvedSelection(result, { shouldScroll: await this.shouldScroll() });
    }
    return result;
  };

  private readonly resolveOperation = async (
    input: ProspectiveResolveInput,
    options?: SelectionOperationOptions,
    entryRuleOverride?: string,
  ): Promise<SelectionResult> => {
    const operationId = this.operationId(options);
    const page = this.pageFromResolveAttributes(input.attributes);
    const params = this.resolveParams(input.attributes);

    const result = await this.perform(operationId, async (sequence) => {
      const response = await this.resolveSelection(params, sequence);
      const { rule, url, count } = response;
      const entryRule = entryRuleOverride || this.currentState?.entryRule;

      if (!entryRule) {
        throw new Error("Failed to resolve rule: missing entry rule");
      }

      return {
        selection: {
          rule,
          page,
          url,
          entryRule,
        },
        ...(count !== undefined ? { count } : {}),
      };
    });

    return result;
  };

  public readonly assign: (
    input: SelectionAssignment,
    options?: SelectionOperationOptions,
  ) => Promise<SelectionResult> = async (input, options) => {
    const result = await this.assignOperation(input, options);
    if (result.count !== 0) {
      this.emitResolvedSelection(result, { shouldScroll: await this.shouldScroll() });
    }
    return result;
  };

  private readonly assignOperation = async (
    input: SelectionAssignment,
    options?: SelectionOperationOptions,
  ): Promise<SelectionResult> => {
    const operationId = this.operationId(options);
    this.validateRule(input.rule);
    this.validateUrl(input.url);

    return await this.perform(operationId, async (sequence) => {
      /*                                                                         */
      /*                                                                           */
      /*                                  */
      const { count } = await this.refreshTrackingIdentity(input.rule, sequence);
      const entryRule = input.entryRule || this.currentState?.entryRule;

      if (!entryRule) {
        throw new Error("Selection assignment requires a non-empty entry rule");
      }

      return {
        selection: {
          rule: input.rule,
          page: input.page,
          url: input.url,
          entryRule,
        },
        ...(count !== undefined ? { count } : {}),
      };
    });
  };

  public async resolveLegacy(
    input: Record<string, string | string[]>,
    options: LegacyResolveOptions,
  ): Promise<SelectionState> {
    const result = await this.resolveOperation(
      { attributes: input },
      undefined,
      options.entryRule || this.currentState?.entryRule,
    );
    this.emitResolvedSelection(result, {
      shouldScroll: await this.shouldScroll(),
      ...(options.entryRule ? { entryRule: options.entryRule } : {}),
    });
    return result.selection;
  }

  public async assignLegacy(
    input: { rule: string; page?: Page; entryRule?: string; url?: string },
    options: LegacyAssignOptions,
  ): Promise<SelectionState> {
    const result = await this.assignOperation(
      {
        rule: input.rule,
        page: mapLegacyPage(input.page),
        url: input.url || window.location.href,
        ...(input.entryRule !== undefined ? { entryRule: input.entryRule } : {}),
      },
      {},
    );

    this.emitResolvedSelection(
      result,
      {
        ...options.event,
        mergeOnPi: true,
        ...(result.count !== undefined ? { count: result.count } : {}),
        ssid: this.ssid(),
      },
      false,
    );
    return result.selection;
  }

  private async perform(
    operationId: string,
    operation: (sequence: number) => Promise<Omit<SelectionResult, "operationId">>,
  ): Promise<SelectionResult> {
    const sequence = ++this.latestOperation;
    const result = await operation(sequence);

    this.ensureCurrentOperation(sequence);

    const completeResult = { ...result, operationId };
    if (completeResult.count !== 0) {
      this.currentState = completeResult.selection;
      otto.selection.current.emit(completeResult.selection, { retain: true });
      otto.selection.changes.emit({ selection: completeResult.selection, operationId });
    }

    return completeResult;
  }

  private bootstrap(): void {
    const rule = this.dependencies.dom.bootstrapRule();
    const entryRule = this.dependencies.dom.historyEntryRule() || rule;
    const state: SelectionState = {
      rule,
      page: pageFromUrl(window.location.href),
      url: this.dependencies.dom.currentUrl(),
      entryRule,
    };

    this.currentState = state;
    otto.selection.current.emit(state, { retain: true });
  }

  private resolveParams(attributes: Record<string, string | string[]>): URLSearchParams {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(attributes)) {
      if (typeof value === "string") {
        params.append(key, value);
      } else if (Array.isArray(value) && value.every((entry) => typeof entry === "string")) {
        value.forEach((entry) => params.append(key, entry));
      } else {
        throw new Error(`Unsupported selection field "${key}"`);
      }
    }

    this.validateRule(attributes.rule);
    return params;
  }

  private pageFromResolveAttributes(attributes: Record<string, string | string[]>): SelectionPage {
    return {
      ...(firstValue(attributes.sortiertnach) ? { sortOrder: firstValue(attributes.sortiertnach) } : {}),
      ...(firstValue(attributes.o) ? { offset: firstValue(attributes.o) } : {}),
      ...(firstValue(attributes.l) ? { layout: firstValue(attributes.l) } : {}),
      ...(firstValue(attributes.c) ? { comparisonId: firstValue(attributes.c) } : {}),
    };
  }

  private async refreshTrackingIdentity(
    rule: string,
    operationSequence: number,
  ): Promise<Pick<ResolvedSelectionResponse, "count">> {
    const response = await this.resolveSelection(new URLSearchParams({ rule }), operationSequence);
    return { ...(response.count !== undefined ? { count: response.count } : {}) };
  }

  private async resolveSelection(
    params: URLSearchParams,
    operationSequence: number,
  ): Promise<ResolvedSelectionResponse> {
    const resolverResponse = await this.dependencies.resolver(params, operationSequence);
    this.ensureCurrentOperation(operationSequence);
    this.dependencies.dom.applyResolvedResponse(resolverResponse);
    return resolverResponse;
  }

  private ensureCurrentOperation(operationSequence: number): void {
    if (operationSequence !== this.latestOperation) {
      throw new Error("Selection operation was superseded by a newer operation");
    }
  }

  private ssid(): string {
    const ssid = this.dependencies.dom.currentSsid();
    if (!ssid) {
      throw new Error("Failed to resolve rule: missing ssid");
    }
    return ssid;
  }

  private validateRule(rule: unknown): asserts rule is string {
    if (typeof rule !== "string" || !rule || rule === "undefined" || rule === "null") {
      throw new Error("Invalid rule");
    }
  }

  private validateUrl(url: unknown): asserts url is string {
    if (typeof url !== "string" || !url) {
      throw new Error("Invalid url");
    }
  }

  private operationId(options?: SelectionOperationOptions): string {
    return options?.operationId || `${Date.now()}-${++this.nextOperationId}`;
  }

  private async shouldScroll(): Promise<boolean> {
    const breakpoint = await currentBreakpoint.get();
    return breakpoint === "s" || breakpoint === "m";
  }

  private emitResolvedSelection(
    result: SelectionResult,
    legacy: Partial<DresonRuleResolvedEvent> = {},
    includeUrl = true,
  ): void {
    const selection = result.selection;
    eventQBus.emit("ftfind.dresonRule.resolved", {
      mergeOnPi: true,
      ...legacy,
      rule: selection.rule,
      page: legacyPage(selection.page),
      ...(includeUrl ? { redirect: legacyUrl(selection.url) } : {}),
      ...(result.count !== undefined ? { count: result.count } : {}),
      ...(legacy.entryRule !== undefined ? { entryRule: legacy.entryRule } : {}),
      ssid: this.ssid(),
    });
  }
}

function firstValue(value: string | string[] | undefined): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}

function pageFromUrl(url: string): SelectionPage {
  const params = new URL(url, window.location.origin).searchParams;
  return {
    ...(params.get("sortiertnach") ? { sortOrder: params.get("sortiertnach")! } : {}),
    ...(params.get("o") ? { offset: params.get("o")! } : {}),
    ...(params.get("l") ? { layout: params.get("l")! } : {}),
    ...(params.get("c") ? { comparisonId: params.get("c")! } : {}),
  };
}

function legacyUrl(url: string): string {
  const parsed = new URL(url, window.location.origin);
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

function legacyPage(page: SelectionPage): Page {
  return {
    ...(page.sortOrder !== undefined ? { sortiertnach: page.sortOrder } : {}),
    ...(page.layout !== undefined ? { l: page.layout } : {}),
    ...(page.offset !== undefined ? { o: page.offset } : {}),
  };
}

function mapLegacyPage(page: Page | undefined): SelectionPage {
  return {
    ...(page?.sortiertnach !== undefined ? { sortOrder: page.sortiertnach } : {}),
    ...(page?.l !== undefined ? { layout: page.l } : {}),
    ...(page?.o !== undefined ? { offset: page.o } : {}),
  };
}

export const selection = new Selection();
